/**
 * Media device selection — mic / speaker / camera.
 * Best practice: enumerate after permission, persist prefs, apply setSinkId when available.
 */

export type MediaDeviceKind = "audioinput" | "audiooutput" | "videoinput";

export type MediaDeviceChoice = {
  deviceId: string;
  label: string;
  kind: MediaDeviceKind;
};

export type MediaDevicePrefs = {
  audioInputId: string | null;
  audioOutputId: string | null;
  videoInputId: string | null;
  /** Last permission state observed */
  micPermission?: PermissionState | "unknown";
};

const STORAGE_KEY = "poker_media_devices_v1";

export function loadMediaDevicePrefs(): MediaDevicePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { audioInputId: null, audioOutputId: null, videoInputId: null };
    }
    const j = JSON.parse(raw) as MediaDevicePrefs;
    return {
      audioInputId: j.audioInputId || null,
      audioOutputId: j.audioOutputId || null,
      videoInputId: j.videoInputId || null,
      micPermission: j.micPermission || "unknown",
    };
  } catch {
    return { audioInputId: null, audioOutputId: null, videoInputId: null };
  }
}

export function saveMediaDevicePrefs(prefs: MediaDevicePrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    window.dispatchEvent(
      new CustomEvent("poker-media-devices-changed", { detail: prefs }),
    );
  } catch {
    /* private mode */
  }
}

/** Request mic (+ optional camera) so labels are visible in enumerateDevices. */
export async function requestMediaPermissions(opts?: {
  video?: boolean;
}): Promise<{ ok: boolean; error?: string; stream?: MediaStream }> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return { ok: false, error: "Media devices not supported in this browser" };
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: opts?.video ? { facingMode: "user" } : false,
    });
    // Stop tracks — we only needed permission + labels
    stream.getTracks().forEach((t) => t.stop());
    const prefs = loadMediaDevicePrefs();
    prefs.micPermission = "granted";
    saveMediaDevicePrefs(prefs);
    return { ok: true, stream };
  } catch (e) {
    const prefs = loadMediaDevicePrefs();
    prefs.micPermission = "denied";
    saveMediaDevicePrefs(prefs);
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

export async function listMediaDevices(): Promise<MediaDeviceChoice[]> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) {
    return [];
  }
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((d) =>
      d.kind === "audioinput" ||
      d.kind === "audiooutput" ||
      d.kind === "videoinput",
    )
    .map((d, i) => ({
      deviceId: d.deviceId || `default-${d.kind}-${i}`,
      label:
        d.label ||
        (d.kind === "audioinput"
          ? `Microphone ${i + 1}`
          : d.kind === "audiooutput"
            ? `Speaker ${i + 1}`
            : `Camera ${i + 1}`),
      kind: d.kind as MediaDeviceKind,
    }));
}

/** Apply speaker to an HTMLMediaElement when setSinkId is supported (Chrome/Edge). */
export async function applyAudioOutput(
  el: HTMLMediaElement | null,
  deviceId: string | null,
): Promise<{ ok: boolean; error?: string }> {
  if (!el || !deviceId) return { ok: true };
  const anyEl = el as HTMLMediaElement & {
    setSinkId?: (id: string) => Promise<void>;
  };
  if (typeof anyEl.setSinkId !== "function") {
    return {
      ok: false,
      error: "Speaker selection not supported in this browser (try Chrome/Edge)",
    };
  }
  try {
    await anyEl.setSinkId(deviceId);
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

export function supportsSetSinkId(): boolean {
  if (typeof HTMLMediaElement === "undefined") return false;
  return typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId ===
    "function";
}

// ── AI voice open / consent (cross-platform) ─────────────────────────────────

const VOICE_CONSENT_KEY = "poker_vidu_voice_consent_v1";

export type VoiceConsentMap = {
  /** Persona id → accepted at epoch ms */
  budb?: number;
  racalvin?: number;
  /** User declined auto-prompt for this session only (memory) */
};

export function isSecureMediaContext(): boolean {
  if (typeof window === "undefined") return false;
  // Localhost is treated as secure by browsers for getUserMedia
  if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
    return true;
  }
  return window.isSecureContext === true;
}

/** UA hints for voice UI (not for security decisions). */
export function detectVoicePlatform(): {
  ios: boolean;
  android: boolean;
  safari: boolean;
  mobile: boolean;
  setSinkId: boolean;
  secure: boolean;
} {
  if (typeof navigator === "undefined") {
    return {
      ios: false,
      android: false,
      safari: false,
      mobile: false,
      setSinkId: false,
      secure: false,
    };
  }
  const ua = navigator.userAgent || "";
  const ios =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const android = /Android/i.test(ua);
  const safari = /^((?!chrome|android).)*safari/i.test(ua) || ios;
  const mobile = ios || android || /Mobi/i.test(ua);
  return {
    ios,
    android,
    safari,
    mobile,
    setSinkId: supportsSetSinkId(),
    secure: isSecureMediaContext(),
  };
}

export function loadVoiceConsent(): VoiceConsentMap {
  try {
    const raw = localStorage.getItem(VOICE_CONSENT_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as VoiceConsentMap;
  } catch {
    return {};
  }
}

export function hasVoiceConsent(persona: "budb" | "racalvin"): boolean {
  const m = loadVoiceConsent();
  return Boolean(m[persona] && Number(m[persona]) > 0);
}

export function saveVoiceConsent(
  persona: "budb" | "racalvin",
  accepted: boolean,
): void {
  try {
    const m = loadVoiceConsent();
    if (accepted) m[persona] = Date.now();
    else delete m[persona];
    localStorage.setItem(VOICE_CONSENT_KEY, JSON.stringify(m));
  } catch {
    /* private mode */
  }
}

/**
 * Short platform tips for AI voice open UI.
 * Keep copy plain — shown next to Enable Voice.
 */
export function voicePlatformTips(): string[] {
  const p = detectVoicePlatform();
  const tips: string[] = [];
  if (!p.secure) {
    tips.push("Voice needs HTTPS (or localhost). Open poker.grudge-studio.com over secure URL.");
  }
  if (p.ios) {
    tips.push(
      "iPhone/iPad: tap Enable Voice once (Safari blocks auto-mic). Use headphones if echo.",
    );
    tips.push("If mic stays off: Settings → Safari → Microphone → Allow for this site.");
  } else if (p.android) {
    tips.push(
      "Android: allow microphone when prompted. Chrome works best for voice↔voice.",
    );
  } else {
    tips.push(
      "Desktop: allow microphone for this site. Chrome/Edge support headset picker in Options.",
    );
  }
  if (!p.setSinkId && !p.ios) {
    tips.push("Speaker pick is limited in this browser — system output is used.");
  }
  tips.push("Talk naturally after LIVE · or type chat — same AI hears both.");
  return tips;
}

/**
 * Full best-practice open sequence for AI voice:
 * 1) secure context  2) user gesture  3) getUserMedia  4) optional preferred device
 */
export async function openVoiceMicForAi(opts?: {
  preferredInputId?: string | null;
}): Promise<{
  ok: boolean;
  error?: string;
  denied?: boolean;
  unsupported?: boolean;
}> {
  if (!isSecureMediaContext()) {
    return {
      ok: false,
      unsupported: true,
      error: "Insecure page — use HTTPS to enable microphone",
    };
  }
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return {
      ok: false,
      unsupported: true,
      error: "This browser does not support microphone access",
    };
  }

  const preferred =
    opts?.preferredInputId || loadMediaDevicePrefs().audioInputId;

  try {
    const audio: boolean | MediaTrackConstraints = preferred
      ? { deviceId: { ideal: preferred } }
      : true;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio,
      video: false,
    });
    // Permission granted — keep track briefly so iframe/Live can re-acquire
    window.setTimeout(() => {
      try {
        stream.getTracks().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
    }, 1200);
    const prefs = loadMediaDevicePrefs();
    prefs.micPermission = "granted";
    saveMediaDevicePrefs(prefs);
    return { ok: true };
  } catch (e) {
    const name = e instanceof DOMException ? e.name : "";
    const denied =
      name === "NotAllowedError" ||
      name === "PermissionDeniedError" ||
      /denied|Permission/i.test(String(e));
    const prefs = loadMediaDevicePrefs();
    prefs.micPermission = denied ? "denied" : "unknown";
    saveMediaDevicePrefs(prefs);
    return {
      ok: false,
      denied,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}
